/**
 * @fileoverview Read-only view of one Sales CRM prospect.
 * @description Opened from a Sales Pipeline backlog or Pending Approval row. Reads the prospect in
 * full from the backend (GET /sales/prospects/{prospectId}) and shows its five wizard sections —
 * bank, address, organisation, primary contact and sales info — plus its owner, notes and
 * ownership history. Catalogue ids (core banking solution) are named from the prospect metadata.
 * The backend decides who may read a prospect, so a prospect the viewer can't see shows the same
 * load error as one that doesn't exist.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useProspectDetails } from '@/features/sales/hooks/useProspectDetails'
import { useProspectMetadata } from '@/features/sales/hooks/useProspectMetadata'
import Badge from '@/shared/components/atoms/Badge'
import Skeleton from '@/shared/components/atoms/Skeleton'
import Modal from '@/shared/components/molecules/Modal'

import type { ProspectAdmissionState, ProspectDetails } from '@/features/sales/types/prospect.types'
import type { ReactNode } from 'react'

interface ProspectDetailModalProps {
  prospectId: string | null
  onClose: () => void
}

interface FieldProps {
  label: string
  children: ReactNode
  wide?: boolean
  /** QA locator: the field gets it, its value gets `<testId>-value`. */
  testId?: string
}

const ADMISSION_BADGE: Record<ProspectAdmissionState, 'info' | 'warning' | 'danger' | 'neutral'> = {
  ACCEPTED: 'info',
  PENDING_REVIEW: 'warning',
  REJECTED: 'danger',
  WITHDRAWN: 'neutral',
}

/**
 * @description One labelled value in a details panel.
 * @param {FieldProps} props - The label and value.
 * @param {string} props.label - The field's label.
 * @param {ReactNode} props.children - Its value.
 * @param {boolean} [props.wide] - Span the panel's full width.
 * @param {string} [props.testId] - QA locator for the field; its value gets `<testId>-value`.
 */
function Field({ label, children, wide = false, testId }: FieldProps) {
  return (
    <div
      className={`flex min-w-0 flex-col gap-0.5 ${wide ? 'col-span-full' : ''}`}
      data-testid={testId}
    >
      <span className="text-[10.5px] font-semibold tracking-[0.7px] text-footer">{label}</span>
      <span
        className="text-[13px] font-medium break-words text-ink"
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {children}
      </span>
    </div>
  )
}

/**
 * @description One titled details panel.
 * @param {{ title: string; children: ReactNode }} props - The panel's title and fields.
 * @param {string} props.title - The panel's title.
 * @param {ReactNode} props.children - Its fields.
 * @param {string} [props.testId] - QA locator for the panel.
 */
function Panel({
  title,
  children,
  testId,
}: {
  title: string
  children: ReactNode
  testId?: string
}) {
  return (
    <div className="flex flex-col gap-2.5" data-testid={testId}>
      <span className="text-[11px] font-bold tracking-[0.8px] text-kicker">{title}</span>
      <div className="grid grid-cols-1 gap-3.5 rounded-lg border border-row-border bg-surface-hover p-4 sm:grid-cols-2">
        {children}
      </div>
    </div>
  )
}

/**
 * @description One stat tile of the summary strip.
 * @param {{ label: string; value: ReactNode; accent?: boolean }} props - The tile's content.
 * @param {string} props.label - The tile's label.
 * @param {ReactNode} props.value - Its value.
 * @param {boolean} [props.accent] - Use the dark accent background.
 * @param {string} [props.testId] - QA locator for the tile; its value gets `<testId>-value`.
 */
function Stat({
  label,
  value,
  accent = false,
  testId,
}: {
  label: string
  value: ReactNode
  accent?: boolean
  testId?: string
}) {
  return (
    <div
      className={`flex flex-col gap-1 px-4 py-3 ${accent ? 'bg-accent-panel' : 'bg-white'}`}
      data-testid={testId}
    >
      <span
        className={`text-[10px] font-semibold tracking-[0.8px] ${accent ? 'text-accent-label' : 'text-slate'}`}
      >
        {label}
      </span>
      <span
        className={`text-sm font-bold ${accent ? 'text-white' : 'text-ink'}`}
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {value}
      </span>
    </div>
  )
}

/**
 * @description The summary strip: branches, tier, bank type and owner.
 * @param {{ prospect: ProspectDetails }} props - The prospect.
 * @param {ProspectDetails} props.prospect - The prospect in full.
 */
function StatsStrip({ prospect }: { prospect: ProspectDetails }) {
  const { t } = useTranslation()
  const ownerName = prospect.currentOwner?.displayName ?? t('sales.pipeline.filters.unassigned')

  return (
    <div
      className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-divider sm:grid-cols-4"
      data-testid="prospect-detail-stats"
    >
      <Stat
        accent
        label={t('sales.tenantDetail.stat.branches')}
        value={prospect.branchCount}
        testId="prospect-detail-stat-branches"
      />
      <Stat
        label={t('sales.tenantDetail.stat.tier')}
        value={prospect.tier.name}
        testId="prospect-detail-stat-tier"
      />
      <Stat
        label={t('sales.tenantDetail.stat.bankType')}
        value={prospect.bankType.name}
        testId="prospect-detail-stat-bank-type"
      />
      <Stat
        label={t('sales.prospectDetail.owner')}
        value={ownerName}
        testId="prospect-detail-stat-owner"
      />
    </div>
  )
}

/**
 * @description The prospect's profile panel: bank, address, organisation, contact and sales info.
 * @param {{ prospect: ProspectDetails; coreBankingName: string }} props - The prospect.
 * @param {ProspectDetails} props.prospect - The prospect in full.
 * @param {string} props.coreBankingName - Its core banking solution's name.
 */
function ProfilePanel({
  prospect,
  coreBankingName,
}: {
  prospect: ProspectDetails
  coreBankingName: string
}) {
  const { t } = useTranslation()
  const { bank, address, organisation, primaryContact } = prospect
  const p = 'sales.tenantDetail.profile'

  return (
    <Panel title={t(`${p}.title`)} testId="prospect-detail-profile">
      <Field label={t(`${p}.regNumber`)} testId="prospect-detail-reg-number">
        {bank.registrationNumber}
      </Field>
      <Field label={t(`${p}.headOffice`)} testId="prospect-detail-head-office">
        {bank.headOfficeLocation}
      </Field>
      <Field label={t(`${p}.website`)} testId="prospect-detail-website">
        {bank.website ?? '—'}
      </Field>
      <Field label={t(`${p}.location`)} testId="prospect-detail-location">
        {prospect.location.city}, {prospect.location.state}
      </Field>
      <Field label={t(`${p}.address`)} wide testId="prospect-detail-address">
        {address.streetAddress}
      </Field>
      <Field label={t(`${p}.district`)} testId="prospect-detail-district">
        {address.district}
      </Field>
      <Field label={t(`${p}.pincode`)} testId="prospect-detail-pincode">
        {address.pinCode}
      </Field>
      <Field label={t(`${p}.employees`)} testId="prospect-detail-employees">
        {organisation.employeeCount ?? '—'}
      </Field>
      <Field label={t(`${p}.coreBanking`)} testId="prospect-detail-core-banking">
        {coreBankingName}
      </Field>
      <Field label={t(`${p}.existingMis`)} testId="prospect-detail-existing-mis">
        {organisation.existingMisSolution ?? '—'}
      </Field>
      <Field label={t(`${p}.leadSource`)} testId="prospect-detail-lead-source">
        {prospect.leadSource.name}
      </Field>
      <Field label={t(`${p}.leadRating`)} testId="prospect-detail-lead-rating">
        {t(`sales.pipeline.ratings.${prospect.leadRating}`)}
      </Field>
      {prospect.createdBy && (
        <Field label={t('sales.prospectDetail.createdBy')} testId="prospect-detail-created-by">
          {prospect.createdBy.displayName}
        </Field>
      )}
      {prospect.target && (
        <Field label={t('sales.prospectDetail.target')} testId="prospect-detail-target">
          {t('sales.prospectDetail.targetValue', {
            name: prospect.target.name,
            period: prospect.target.periodLabel,
            due: prospect.target.dueDate,
          })}
        </Field>
      )}
      {prospect.archivedAt && (
        <Field
          label={t('sales.prospectDetail.archivedBy')}
          wide
          testId="prospect-detail-archive-info"
        >
          {prospect.archivedBy?.displayName ?? '—'}
          {prospect.archiveReason && (
            <span className="block text-[12.5px] font-normal text-cell">
              {prospect.archiveReason}
            </span>
          )}
        </Field>
      )}
      <Field label={t(`${p}.contact`)} wide testId="prospect-detail-contact">
        {primaryContact.name}
        {primaryContact.designation ? ` · ${primaryContact.designation}` : ''}
        <span className="block text-[12.5px] font-normal text-cell">
          {primaryContact.email} · {primaryContact.countryCode} {primaryContact.mobileNumber}
        </span>
      </Field>
    </Panel>
  )
}

/**
 * @description The prospect's sections, once loaded.
 * @param {{ prospect: ProspectDetails; coreBankingName: string }} props - The prospect.
 * @param {ProspectDetails} props.prospect - The prospect in full.
 * @param {string} props.coreBankingName - Its core banking solution's name.
 */
function ProspectSections({
  prospect,
  coreBankingName,
}: {
  prospect: ProspectDetails
  coreBankingName: string
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-5">
      <StatsStrip prospect={prospect} />
      <ProfilePanel prospect={prospect} coreBankingName={coreBankingName} />

      {prospect.notes.length > 0 && (
        <Panel title={t('sales.tenantDetail.profile.notes')} testId="prospect-detail-notes">
          {prospect.notes.map((note) => (
            <Field
              key={note.noteId}
              label={note.createdAt.slice(0, 10)}
              wide
              testId="prospect-detail-note"
            >
              {note.text}
            </Field>
          ))}
        </Panel>
      )}
    </div>
  )
}

/**
 * @component
 * @param {ProspectDetailModalProps} props - Which prospect, and the close handler.
 * @param {string | null} props.prospectId - The prospect to show, or null to keep it closed.
 * @param {() => void} props.onClose - Called when the dialog is dismissed.
 */
function ProspectDetailModal({ prospectId, onClose }: ProspectDetailModalProps) {
  const { t } = useTranslation()
  const detailsQuery = useProspectDetails(prospectId)
  const metadataQuery = useProspectMetadata()

  if (prospectId === null) return null

  const prospect = detailsQuery.data?.prospect

  if (detailsQuery.isPending) {
    return (
      <Modal
        open
        onClose={onClose}
        title={t('sales.activeClients.title')}
        size="lg"
        data-testid="prospect-detail-modal"
      >
        <div className="flex flex-col gap-3" data-testid="prospect-detail-loading">
          <Skeleton height={20} width="60%" />
          <Skeleton height={60} />
          <Skeleton height={60} />
        </div>
      </Modal>
    )
  }

  if (detailsQuery.isError || !prospect) {
    return (
      <Modal
        open
        onClose={onClose}
        title={t('sales.activeClients.title')}
        size="lg"
        data-testid="prospect-detail-modal"
      >
        <p className="text-sm text-danger" data-testid="prospect-detail-load-error">
          {t('sales.activeClients.loadError')}
        </p>
      </Modal>
    )
  }

  const coreBankingName =
    metadataQuery.data?.coreBankingSolutions.find(
      (entry) => entry.id === prospect.organisation.coreBankingSolutionId,
    )?.name ?? '—'

  return (
    <Modal
      open
      onClose={onClose}
      data-testid="prospect-detail-modal"
      title={prospect.bankName}
      subtitle={
        <div className="flex flex-wrap items-center gap-2.5">
          <span data-testid="prospect-detail-tenant-code">{prospect.tenantCode}</span>
          <span className="h-2.5 w-px bg-rule" />
          <span data-testid="prospect-detail-bank-type">{prospect.bankType.name}</span>
          <span className="h-2.5 w-px bg-rule" />
          <Badge
            variant={ADMISSION_BADGE[prospect.admissionState]}
            dot
            data-testid="prospect-detail-admission-badge"
          >
            {t(`sales.prospectDetail.admission.${prospect.admissionState}`)}
          </Badge>
          {prospect.archivedAt && (
            <Badge variant="warning" data-testid="prospect-detail-archived-badge">
              {t('sales.prospectDetail.archived')}
            </Badge>
          )}
        </div>
      }
      size="lg"
    >
      <ProspectSections prospect={prospect} coreBankingName={coreBankingName} />
    </Modal>
  )
}

export default ProspectDetailModal
